import { Center, Loader } from '@mantine/core';
import { tKeys, useTranslate } from 'locales/index';
import type { ReminderDocumentType } from '../api/preview-reminder-request';
import { useLoadAdditionalResources } from '../api/use-load-additional-resources';
import { type ReminderResourceGroup, ReminderResourceTable } from './ReminderResourceTable';

type ReminderInvoiceSelectionProps = {
  debtorId: number;
  documentType: ReminderDocumentType;
  selectedIds: Array<string>;
  onToggle: (id: string) => void;
  selectedCreditNoteIds: Array<string>;
  onToggleCreditNote: (id: string) => void;
  selectedAccountingPaymentIds: Array<string>;
  selectedTpPaymentIds: Array<string>;
  onTogglePayment: (id: string, type: 'accounting' | 'third_party') => void;
};

const capitalize = (value: string) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

export const ReminderInvoiceSelection = ({
  debtorId,
  documentType,
  selectedIds,
  onToggle,
  selectedCreditNoteIds,
  onToggleCreditNote,
  selectedAccountingPaymentIds,
  selectedTpPaymentIds,
  onTogglePayment,
}: ReminderInvoiceSelectionProps) => {
  const t = useTranslate();
  const { additionalResources, isAdditionalResourcesInitialLoading } = useLoadAdditionalResources({
    debtorId,
    actionType: documentType,
  });

  if (isAdditionalResourcesInitialLoading) {
    return (
      <Center p="xl">
        <Loader role="progressbar" aria-label={t(tKeys.LOADING)} />
      </Center>
    );
  }

  const invoiceGroup: ReminderResourceGroup = {
    title: t(tKeys.INVOICES),
    rows: additionalResources.invoices.map(invoice => ({
      id: String(invoice.id),
      ariaLabel: invoice.reference,
      label: invoice.reference,
      date: invoice.dueDate,
      daysLate: invoice.daysLate,
      amount: invoice.localizedMoneyObject.remainingBalance,
      checked: selectedIds.includes(String(invoice.id)),
      onToggle: () => onToggle(String(invoice.id)),
    })),
  };

  const creditNoteGroup: ReminderResourceGroup = {
    title: t(tKeys.CREDIT_NOTES),
    rows: additionalResources.creditNotes.map(creditNote => ({
      id: String(creditNote.id),
      ariaLabel: creditNote.reference,
      label: creditNote.reference,
      date: creditNote.issueDate,
      amount: creditNote.localizedMoneyObject.remainingBalance,
      checked: selectedCreditNoteIds.includes(String(creditNote.id)),
      onToggle: () => onToggleCreditNote(String(creditNote.id)),
    })),
  };

  const paymentGroup: ReminderResourceGroup = {
    title: t(tKeys.PAYMENTS),
    rows: additionalResources.payments.map(payment => ({
      id: `${payment.type}-${payment.id}`,
      ariaLabel: `${payment.type} ${payment.date.format('DD/MM/YYYY')}`,
      label: capitalize(payment.type.replace('_', ' ')),
      date: payment.date,
      amount: payment.localizedMoneyObject.amount,
      checked: (payment.type === 'accounting'
        ? selectedAccountingPaymentIds
        : selectedTpPaymentIds
      ).includes(String(payment.id)),
      onToggle: () => onTogglePayment(String(payment.id), payment.type),
    })),
  };

  const groups = [invoiceGroup, creditNoteGroup, paymentGroup].filter(
    group => group.rows.length > 0,
  );

  return <ReminderResourceTable groups={groups} />;
};
